<script>
// localhost/?quicklink=a32b7252a6edf5e0d8c86047be231933

import { defineAsyncComponent } from "@common/defineAsyncComponent";
import { componentStore } from "@common/componentStore/componentStore";
import {
    DataBinding,
    HierarchicalTree,
    DiagramTools
} from "@syncfusion/ej2-vue-diagrams";
import { DataManager } from "@syncfusion/ej2-data";

const editorSettings = {
    templateSettings: {
        type: 'panel',
        label: 'Template Settings',
        id: 'templateSettings',
        fields: [
            'nodeTemplate',
            'enableTooltip',
            'tooltipTemplate',
        ]
    },
    hierarchySettings: {
        type: 'panel',
        label: 'Hierarchy View Settings',
        id: 'hierarchySettings',
        fields: [
            'orientation'
        ]
    },
    nodeSettings: {
        type: 'panel',
        label: 'Node Settings',
        id: 'nodeSettings',
        fields: [
            'nodeWidth',
            'nodeHeight',
            'nodeIdKey',
            'nodeParentIdKey',
        ]
    },
    tooltipTemplate: {
        id: 'tooltipTemplate',
        name: 'tooltipTemplate',
        type: 'text',
        label: 'Tooltip Template',
        helpText: 'Set the template for the tooltip.',
        default: '<p>Tooltip record: {{record}}</p>'
    },
    nodeTemplate: {
        id: 'nodeTemplate',
        name: 'nodeTemplate',
        type: 'text',
        label: 'Node Template',
        helpText: 'Set the template for the node.',
        default: '<p>Node record: {{record.ORG_TITLE}}</p>'
    },
    enableTooltip: {
        id: 'enableTooltip',
        type: 'boolean',
        label: 'Enable Node Tooltip',
        helpText: 'When the node is clicked, it will open a tooltip which will show the configured tooltip template.',
    },
    orientation: {
        label: 'Orientation',
        type: 'dropdown',
        name: 'orientation',
        items: [
            'TopToBottom',
            'BottomToTop',
            'LeftToRight',
            'RightToLeft'
        ],
        id: 'orientation',
        helpText: 'Set the orientation of the hierarchy view.',
        default: 'Horizontal'
    },
    nodeIdKey: {
        label: 'Node ID Key',
        id: 'nodeId',
        type: 'string',
        helpText: 'The property in your data that contains the ID for the given node.',
        default: 'CHILD_ORGANIZATION_ID'
    },
    nodeParentIdKey: {
        label: 'Node Parent ID Key',
        id: 'nodeId',
        type: 'string',
        helpText: 'The property in your data that contains the ID for the given node.',
        default: 'PARENT_ORGANIZATION_ID'
    },
    nodeWidth: {
        label: 'Node Width',
        type: 'number',
        id: 'nodeWidth',
        helpText: 'Width of the nodes',
        default: 150
    },
    nodeHeight: {
        label: 'Node Height',
        type: 'number',
        id: 'nodeHeight',
        helpText: 'Height of the nodes',
        default: 50
    }
};

const hierarchyViewConfig = {
    cId: "hierarchyView",
    cType: "hierarchyView",
    cTitle: "Hierarchy View",
    cTemplate: `<div :id="cId">
        <div style="width: 100%; height: 1200px;" v-if="nodesGenerated">
            <ejs-diagram
                style='display:block'
                id="diagram"
                ref="hierarchyDiagram"
                :width='width'
                :height='height'
                :nodeTemplate="nodeTemplate"
                :getNodeDefaults='getNodeDefaults'
                :getConnectorDefaults='getConnectorDefaults'
                :snapSettings='snapSettings'
                :layout='layout'
                :dataSourceSettings='dataSourceSettings'
                :tool='tool'
            ></ejs-diagram>
        </div>
    </div>`,
    controls: []
};

// let nodeTemplate;

const hierarchyViewCmp = {
    name: "hierarchyView",
    editorSettings: editorSettings,
    data() {
        return {
            hierarchyNodes: false,
            nodeData: false,
            nodesGenerated: false,
            width: "100%",
            height: "100%",
            nodeTemplate: false,
            // Sets the default values of nodes
            // Disables all interactions except zoom/pan
            tool: DiagramTools.ZoomPan,
            snapSettings: { constraints: 0 }
        };
    },
    computed: {
        dataSourceSettings() {
            return {
                id: "recordId",
                parentId: "parentId",
                dataSource: this.nodeData,
                // id: "Name",
                // parentId: "Category",
                // dataSource: new DataManager(species),
                // binds the external data with node
                // doBinding: (nodeModel, data/* , diagram */) => {
                // nodeModel.annotations = [
                //     {
                //         /* tslint:disable:no-string-literal */
                //         content: data["RECORD_ID"],
                //         // content: data,
                //         style: { color: "black" }
                //     }
                // ];
                // nodeModel.style = {
                //     fill: "#ffeec7",
                //     strokeColor: "#f5d897",
                //     strokeWidth: 1
                // };
                // }
            };
        },
        layout() {
            return {
                type: "HierarchicalTree",
                orientation: this.config.orientation || 'TopToBottom',
                horizontalSpacing: 60,
                verticalSpacing: 50,
                margin: {
                    top: 10,
                    left: 10,
                    right: 10,
                    bottom: 0
                }
            };
        },
        getNodeDefaults() {
            return (obj, _diagram) => {
                // Initialize shape
                obj.shape = {
                    type: "HTML"
                };
                obj.style = { strokeWidth: 1 };
                obj.width = this.config.nodeWidth || 150;
                obj.height = this.config.nodeHeight || 50;
                obj.expandIcon = {
                    height: 15,
                    width: 15,
                    shape: "Minus",
                    fill: 'lightgray',
                    offset: {
                        x: 0.5,
                        y: 0.85
                    }
                };
                obj.collapseIcon.offset = {
                    x: 0.5,
                    y: 0.85
                };
                obj.collapseIcon.height = 15;
                obj.collapseIcon.width = 15;
                obj.collapseIcon.shape = "Plus";
            };
        },
        // Sets the default values of connector
        getConnectorDefaults() {
            return (connector, diagram) => {
                connector.type = "Orthogonal";
                if (connector.style) connector.style.strokeColor = "#3a3a3a";
                if (connector.targetDecorator) connector.targetDecorator.shape = "None";
            };
        }
    },
    methods: {
        generateNodeTemplate() {
            let _parent = this;
            if (!this.config.nodeTemplate) this.config.nodeTemplate = '<div>Configure the node template</div>';
            let nodeHTML = this.config.enableTooltip
                ? `<div class="node-wrapper">
                    <sam-tooltip
                        v-if="tooltipContent[tooltipTemplateName]"
                        css-class="hierarchy-node-tooltip"
                        label-classes="hierarchy-node-tooltip-label"
                        :id="tooltipTemplateName"
                        :content="tooltipContent[tooltipTemplateName]"
                        position="BottomCenter"
                    >${this.config.nodeTemplate}</sam-tooltip>
                </div>`
                : `<div class="node-wrapper">${this.config.nodeTemplate}</div>`;
            let nodeTemplateCmp = vueApp.component('nodeTemplate', {
                name: 'nodeTemplate',
                template: nodeHTML,
                data() {
                    return {
                        data: {},
                        tooltipContent: {}
                    };
                },
                computed: {
                    record() {
                        return this.data.data;
                    },
                    parent() {
                        return _parent;
                    },
                    config() {
                        return this.parent.config;
                    },
                    tooltipTemplateName() {
                        // TODO connect this to configuration to set the record ID
                        return 'tooltipContent_' + this.record.RELATIONSHIP_ID;
                    }
                },
                mounted() {
                    let nodeData = this.data;
                    let cmpConfig = _parent.config;
                    let nodeRecord = this.record;
                    let compName = this.tooltipTemplateName;
                    let tooltipTemplate = function () {
                        return {
                            template: vueApp.component(compName, {
                                data() {
                                    return {};
                                },
                                computed: {
                                    parent() {
                                        return _parent;
                                    },
                                    record() {
                                        return nodeRecord;
                                    },
                                    data() {
                                        return nodeData;
                                    },
                                    recordStore() {
                                        return this.parent.recordStore;
                                    },
                                },
                                template: cmpConfig.tooltipTemplate,
                            })
                        };
                    };
                    Object.defineProperty(tooltipTemplate, 'name', { value: compName });
                    this.tooltipContent[compName] = tooltipTemplate;
                },
                components: {
                    samTooltip: defineAsyncComponent(() => import('@common/control/samTooltip.vue')),
                }
            });
            let nodeTemplate = function () {
                return {
                    template: nodeTemplateCmp
                };
            };
            this.nodeTemplate = nodeTemplate;
        },
        async onLoad() {
            let nodes = [];
            let topParents = {};
            let includeAllHierarchy = !!this.contextOrgId;
            // TODO allow source to be configurable
            // TODO move this to a data formatter
            let response = await this.sDs.post('sds_getOrgRelationshipList', this.contextOrgId, includeAllHierarchy, ['ORG_TITLE', 'ORG_KIND_ID', 'ORG_KIND']);
            let relData = response && response.data;
            let orgs = library.cloneObj(relData.ORGANIZATION);
            let nodeIdKey = this.config.nodeIdKey || 'CHILD_ORGANIZATION_ID';
            let nodeParentIdKey = this.config.nodeParentIdKey || 'PARENT_ORGANIZATION_ID';
            delete relData.ORGANIZATION;
            relData = Object.values(relData);
            if (Array.isArray(relData)) {
                relData.forEach((relationship) => {
                    relationship.recordId = relationship[nodeIdKey].toString();
                    relationship.parentId = relationship[nodeParentIdKey].toString();
                    let org = orgs[relationship.recordId];
                    relationship = { ...relationship, ...org };
                    nodes.push(relationship);
                });
            }
            const recordIds = new Set(nodes.map((node) => node.recordId));
            nodes.forEach((node) => {
                if (!recordIds.has(node.parentId) && !topParents[node.parentId]) {
                    let topParentRecord = orgs[node.parentId];
                    if (topParentRecord) {
                        topParentRecord.recordId = node.parentId;
                        topParents[node.parentId] = topParentRecord;
                    }
                }
            });
            topParents = Object.values(topParents);
            nodes = nodes.concat(topParents);
            nodes = nodes.map((node) => {
                node.color = library.stringToColor(node.ORG_KIND.toString());
                node.bgColor = library.stringToColor(node.ORG_KIND.toString(), '3%');
                return node;
            });
            this.hierarchyNodes = nodes;
            this.nodeData = new DataManager(this.hierarchyNodes);
            this.generateNodeTemplate(); // Binds this component as parent
            this.nodesGenerated = true;
        }
    },
    components: {
        ejsDiagram: defineAsyncComponent(() => import('@common/syncfusion/diagrams.vue'))
    },
    provide: {
        diagram: [DataBinding, HierarchicalTree]
    }
};
// componentStore.initComponent puts the component into the component store, makes the component available to other vue components, and adds it to the list of component options used in editors
componentStore.initComponent(hierarchyViewCmp, hierarchyViewConfig);

export { hierarchyViewConfig, hierarchyViewCmp };
export default hierarchyViewCmp;
</script>

<style scoped>
    :deep(.node-wrapper) {
        height: 100%;
        width: 100%;
        background: transparent;
        text-align: center;
    }
    :deep(.node-wrapper div) {
        height: 100%;
        width: 100%
    }
    :deep(.hierarchy-node-tooltip-label) {
        color: black;
    }
    :deep(div.node-content-container) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        padding: 1rem;
        border: 1px solid transparent;
        border-radius: 10px;
        box-shadow: 0px 1px 3px 0px #000000ba, 0px 1px 2px -1px #000000ba;
        cursor: pointer;
    }
    :deep(div.node-content-container:hover) {
        box-shadow: 1px 2px 6px 0px #000000b8, 0px 1px 2px -1px #000000a3;
    }
    :deep(div.tier-color) {
        position: absolute;
        top: 0;
        left: 0;
        width: 0;
        height: 0;
        border-radius: 10px 0 0 0;
        border-right: 30px solid transparent;
        border-bottom: 30px solid transparent;
        border-top: 30px solid;
    }
</style>
